import AbstractController from '../../../../../base/controller/Abstract.controller.js';
import dom from 'fasterdom';
import $ from 'jquery';
//require('ie9-oninput-polyfill')

/**
 * Default RegExp definitions
 * info: hasUserName will be set on init, cause actual username isn't available yet
 */
const regex = {
	hasNumber :      /\d/,
	hasUpperLower :  /^(?=.*([a-z]))(?=.*([A-Z])).+$/,
	hasSpecialChar : /^(?=.*([_!@%#.,:;"'&*()+\-?$\/\\])).+$/,
	hasMinLength :   /^.{8,}$/,
	hasUserName :    ''
};

/**
 * Validation level mapping
 * - levels must ascend in strength ( e.g. 0 = invalid, ... last_N = strongest )
 */
const validationLevelTypes = [ // eslint-disable-line one-var
	'invalid',
	'medium',
	'strong'
];

/**
 * Update password meter component
 * - set/unset validation errors
 * - set validation level bar
 * - show/hide error messages
 * @return {void}
 */
function updateDOM () {
	dom.mutateInstant( () => {
		const cls = `strength-bar--${validationLevelTypes.join( ' strength-bar--' )}`;

		// update level bar
		this.$levelBar
				.removeClass( cls )
				.addClass( `strength-bar--${this.getValidationLevel()}` );

		this.$strengthLabel.text( this.getValidationLevel() );

		// update list items
		this.$invalid
				.filter( '.valid' ).removeClass( 'valid' )
				.filter( '[required]' ).addClass( 'invalid' );

		this.$valid
				.removeClass( 'invalid' )
				.addClass( 'valid' );

		// remove error class if password is set
		if ( !!this.pw ) {
			this.$wrapperPW.removeClass( 'error' );
		}

		// update password meter component
		this.$scope.removeClass( 'password-meter--invalid' );

		if ( !this.isValid && this.error ) {
			this.$scope.addClass( 'password-meter--invalid' );
		}

		// update password comfirm error
		this.$wrapperPWConfirm.removeClass( 'error' );

		if ( !!this.pwConfirm && !this.isConfirmed && this.isValid && !this.error ) {
			this.$wrapperPWConfirm.addClass( 'error' );
		}
	} );
}

/**
 * Validate entered password
 * - check validation strength
 * - update validation level
 * - update valid and invalid collection
 * @param {boolean} force - validate even password is empty string
 * @return {void}
 */
function validatePW ( force ) {

	this.error    = false;
	this.$valid   = $( [] );
	this.$invalid = $( [] );
	this.level    = validationLevelTypes.length - 1;

	for ( let i = this.level; i > 0; i-- ) {

		Object.keys( this.conditions[ i ] ).forEach( ( key ) => {
			const val  = this.conditions[ i ][ key ],
				  regx = this.regex[ key ];

			val.forEach( ( el ) => {
				const $el = $( el );

				if ( ( force || this.pw ) && !!regx && regx.exec( this.pw ) !== null ) {
					this.$valid = this.$valid.add( $el );
				} else {
					this.level    = i - 1;
					this.$invalid = this.$invalid.add( $el );

					if ( force ) {
						$el.addClass( 'valid' );
					}

					if ( $el.is( '.valid[required]' ) || $el.is( '.invalid[required]' ) ) {
						this.error = true;
					}
				}
			} );
		} );
	}
}

/**
 * Handles password input blur events
 * - updates password and password confirm values
 * - updates dom
 * @return {void}
 */
function handleBlur () {
	dom.measureInstant( () => {
		this.pw          = this.$inputPW.val();
		this.pwConfirm   = this.$inputPWConfirm.val();
		this.isConfirmed = !!this.pw && !!this.pwConfirm && this.pw === this.pwConfirm;
	} );

	updateDOM.call( this );
}

/**
 * Handles password input change events
 * - updates password and runs it's validation
 * - updates dom
 * @param {boolean} force - validate even password is empty string
 * @return {void}
 */
function handleInput ( force ) {
	dom.measureInstant( () => {
		this.pw = this.$inputPW.val();

		validatePW.call( this, force );
	} );

	updateDOM.call( this );
}

/**
 * Adds event listeners on password input elements
 * @return {void}
 */
function addHandler () {
	this.$inputPW.on( 'input', handleInput.bind( this, false ) );
	this.$inputPW.on( 'change', handleBlur.bind( this ) );

	this.$inputPWConfirm.on( 'blur', handleInput.bind( this, true ) );
	this.$inputPWConfirm.on( 'change', handleBlur.bind( this ) );
}

/**
 * Sets up validation conditions from list items data attributes
 * - creates array with conditions based on validation leves
 * @return {void}
 */
function setupConditions () {
	dom.measureInstant( () => {
		this.conditions = validationLevelTypes.map( ( type ) => {
			const obj = {};

			this.$scope.find( `.password-meter--${type} li[data-validate]` ).each( ( i, el ) => {
				const $el  = $( el ),
					  regx = $el.attr( 'data-validate' );

				if ( !obj.hasOwnProperty( regx ) ) {
					obj[ regx ] = [];
				}

				obj[ regx ].push( $el );
			} );

			return obj;
		} );
	} );
}

/**
 * Setup regular expressions for validation
 * @return {void}
 */
function setupRegex () {
	dom.measureInstant( () => {
		const username = this.$inputUser ?
						 this.$inputUser.val() :
						 '';

		this.regex             = Object.assign( {}, regex );
		this.regex.hasUserName = new RegExp( `^(?!=($))(?!.*${username}).+$`, 'i' );
	} );
}

/**
 * Class representing a password meter instance.
 * Controls the state of the password meter conditions.
 * Controls password meter behavior.
 * @extends { AbstractController }
 */
class PasswordMeterController extends AbstractController {

	constructor () {
		super();

		this.$scope = null;
	}

	/**
	 * Initialises the controller.
	 * Has to be called after ready event.
	 *
	 * @param { object } $root - jQuery wrapped DOM node
	 * @returns { void }
	 * @public
	 */
	init ( $root ) {

		this.$scope            = $root;
		this.$levelBar         = $root.find( '.strength-bar' );
		this.$strengthLabel    = this.$levelBar.find('.strength-bar__label');
		this.$inputUser        = $root.find('input[type="hidden"]').eq( 0 );
		this.$inputPW          = $root.find('input[type="password"]').eq( 0 );
		this.$inputPWConfirm   = $root.find('input[type="password"]').eq( 1 );
		this.$wrapperPW        = this.$inputPW.closest( '.field-group' );
		this.$wrapperPWConfirm = this.$inputPWConfirm.closest( '.field-group' );
		this.pw                = null;
		this.pwConfirm         = null;

		// available validation levels (see validationLevelTypes): {index} => {type name};
		this.level       = 0;
		this.conditions  = [];
		this.$valid      = [];
		this.$invalid    = [];
		this.isConfirmed = false;

		setupConditions.call( this );
		setupRegex.call( this );
		addHandler.call( this );
	}

	get isValid () {
		return this.level > 0;
	}

	/**
	 * Get type name of current validation level
	 * @return {string} - type name
	 */
	getValidationLevel () {
		return validationLevelTypes[ this.level ];
	}

}

/**
 * @public
 * @return {PasswordMeterController} PasswordMeterController class
 */
export default PasswordMeterController;



// WEBPACK FOOTER //
// ./js/forms/v2/modules/passwordMeter/controller/PasswordMeter.controller.js